<template>
  <b-container>
    <b-modal v-model="cardsEditVisible" :title="editorTitle" @ok="onModalOk" :ok-disabled="aiSubmitting" :ok-title="activeTab === aiTabIndex ? (aiSubmitting ? 'updating...' : 'update with AI') : 'OK'" size="xl" modal-class="view-editor-modal">
      <template #title>
        {{ editorTitle }}
        <modal-help v-if="isCalendar" topic="edit-calendar" />
        <modal-help v-else-if="isMap" topic="edit-map" />
        <modal-help v-else-if="isDetail" topic="edit-card" />
        <modal-help v-else topic="edit-cards" />
      </template>
      <div class="edit-modal-content">
        <b-tabs v-model="activeTab" content-class="tab-body" nav-class="view-editor-tabs" pills>

          <b-tab title="General">
            <div class="tab-section">
              <div class="section-heading">Naming</div>
              <div class="field-row">
                <label class="field-label">
                  unique title
                  <b-form-input v-model="uniqueTitle" :placeholder="editorNoun + ' unique title'" required />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  title
                  <b-form-input v-model="title" :placeholder="editorNoun + ' title'" required />
                </label>
              </div>
            </div>
            <div class="tab-section">
              <div class="section-heading">Data Source</div>
              <div class="data-source-display">
                <span class="data-source-spreadsheet">{{ dataSourceLabel }}</span>
                <span v-if="filterByPageRecord" class="data-source-badge">filtered by page record</span>
              </div>
              <label v-if="pageRecordLink && !isDetail" class="checkbox-label page-record-filter">
                <input type="checkbox" v-model="filterByPageRecord" />
                only show records related to the page's {{ pageRecordLink.pageRecordTitle }} record
              </label>
              <p v-if="pageRecordLink && !isDetail" class="section-desc">this page is opened from a link that carries a {{ pageRecordLink.pageRecordTitle }} record. the view keeps records related through {{ pageRecordLink.columnTitle }}.</p>
              <p v-if="isDetail" class="section-desc">This view shows the one record that opened the page. Visitors can read the fields and cannot edit them.</p>
            </div>
            <div class="tab-section">
              <div class="section-heading">Realtime</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="realtime" />
                enable realtime updates (data will update automatically when records change)
              </label>
            </div>
            <div v-if="isCardsLayout && !isDetail" class="tab-section">
              <div class="section-heading">Layout</div>
              <div class="field-row cards-per-row-field">
                <label class="field-label">
                  maximum cards per row
                  <b-form-input v-model="cardsPerRow" type="number" min="1" :max="maxCardsPerRow" step="1" placeholder="automatic" />
                </label>
              </div>
              <p class="section-desc">Leave this empty and cards wrap as they fit. Enter a number and at most that many cards sit on a row. Each one takes an equal share of the column. 1 lists them one after another, each filling the row.</p>
            </div>
          </b-tab>

          <b-tab v-if="isMap" title="Location">
            <div class="tab-section">
              <div class="section-heading">Location</div>
              <p class="text-muted">An address is looked up when the record is saved. Latitude and longitude columns are used when the spreadsheet already stores coordinates. If both are set, the address column is used.</p>
              <div class="field-row">
                <label class="field-label">
                  address column
                  <b-form-select v-model="addressColumn" :options="addressColumnOptions" />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  latitude column
                  <b-form-select v-model="latitudeColumn" :options="numberColumnOptions" />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  longitude column
                  <b-form-select v-model="longitudeColumn" :options="numberColumnOptions" />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  map style URL
                  <b-form-input v-model="mapStyleUrl" placeholder="https://tiles.openfreemap.org/styles/liberty" />
                </label>
              </div>
            </div>
          </b-tab>

          <b-tab v-if="isCalendar" title="Calendar">
            <div class="tab-section">
              <div class="section-heading">Calendar</div>
              <p class="text-muted">Each record is placed on the day of its date column. The title and description are the text shown on that day. Display rules on a field style that text.</p>
              <div class="field-row">
                <label class="field-label">
                  date column
                  <b-form-select v-model="dateColumn" :options="dateColumnOptions" />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  title column
                  <b-form-select v-model="titleColumn" :options="textColumnOptions" />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  description column
                  <b-form-select v-model="descriptionColumn" :options="textColumnOptions" />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  default layout
                  <b-form-select v-model="calendarLayout" :options="calendarLayoutOptions" />
                </label>
              </div>
              <div class="field-row">
                <label class="field-label">
                  repeat column
                  <b-form-select v-model="recurrenceColumn" :options="recurrenceColumnOptions" />
                </label>
              </div>
              <p class="text-muted">The repeat column holds one rule on the row. Week, day, and month views expand that rule. They do not add a row for each day.</p>
              <div class="field-row">
                <label class="field-label">
                  form for new records
                  <b-form-select v-model="addFormUniqueTitle" :options="formOptions" />
                </label>
              </div>
              <p class="text-muted">Add record fills that form with the chosen day. The visitor saves it with the form.</p>
            </div>
          </b-tab>

          <b-tab title="Fields">
            <div v-if="isCardsLayout" class="tab-section">
              <p class="section-desc">{{ fieldsIntro }}</p>
              <p v-if="columnWidthSaveError" class="card-column-width-error">{{ columnWidthSaveError }}</p>
              <button
                v-if="showAddAllColumns"
                type="button"
                @click="addAllSpreadsheetColumns"
                class="add-btn add-all-columns-btn"
              >
                + add all columns from {{ spreadsheetTitle }}
              </button>
              <p class="section-desc">Drag a field into another column to move it.</p>
              <div v-for="(row, rowIndex) in cards.components" :key="'row-' + rowIndex" class="form-row-block">
                <div class="form-row-header">
                  <span class="form-row-label">row {{ rowIndex + 1 }}</span>
                  <span @click="removeComponent(cards, row)" class="delete-inline">x</span>
                </div>
                <div class="form-row-columns">
                  <div v-for="(column, columnIndex) in row.components" :key="'column-' + columnIndex" class="form-col-block">
                    <div class="form-edit-header">
                      <span class="form-col-label">
                        column {{ columnIndex + 1 }}
                        <span v-if="columnWidthLabel(column)" class="card-column-width-label">{{ columnWidthLabel(column) }}</span>
                      </span>
                      <span @click="removeComponent(row, column)" class="delete-inline">x</span>
                    </div>
                    <div class="card-column-width">
                      <label class="card-column-width-caption">max width</label>
                      <b-form-input
                        :model-value="columnWidthText(column)"
                        type="number"
                        min="1"
                        :max="columnWidthUnit(column) === 'pixel' ? 4000 : 100"
                        placeholder="auto"
                        size="sm"
                        class="card-column-width-input"
                        @update:model-value="setColumnWidth(column, $event)"
                      />
                      <b-form-select
                        :model-value="columnWidthUnit(column)"
                        :options="columnWidthUnitOptions"
                        size="sm"
                        class="card-column-width-unit"
                        @update:model-value="setColumnUnit(column, $event)"
                      />
                    </div>
                    <p v-if="columnWidthError(column)" class="card-column-width-error">{{ columnWidthError(column) }}</p>
                    <draggable
                      v-model="column.components"
                      group="card-fields"
                      :item-key="columnDragKey"
                      handle=".column-drag-handle"
                      ghost-class="column-drag-ghost"
                      :animation="150"
                      :force-fallback="true"
                      :fallback-on-body="true"
                    >
                      <template #item="{ element: component }">
                        <div class="form-edit-item">
                          <DragHandle
                            handle-class="column-drag-handle"
                            :reorder-id="component.uniqueTitle || component.label"
                            @move="moveFieldInColumn(column, component, $event)"
                          />
                          <div class="editable-component" @click="editComponent(column, component)">
                            <div class="component-type">{{component.type}}</div>
                            <div class="component-details" v-if="component.uniqueTitle || component.label || component.childPageUniqueTitle">
                              <span v-if="component.uniqueTitle || component.relatedField" class="component-detail"><span class="detail-label">field:</span> {{ columnFieldLabel(component) }}<span v-if="getColumnType(component.uniqueTitle)" class="column-type-badge">{{getColumnType(component.uniqueTitle)}}</span></span>
                              <span v-if="component.label" class="component-detail"><span class="detail-label">label:</span> {{component.label}}</span>
                              <span v-if="component.childPageUniqueTitle" class="component-detail"><span class="detail-label">links to:</span> {{component.childPageUniqueTitle}}</span>
                              <span v-if="component.linkMode" class="component-detail"><span class="detail-label">mode:</span> {{component.linkMode === 'code' ? 'custom code' : component.linkMode === 'relatedRecord' ? `related record (${component.relationshipColumn})` : component.linkMode === 'static' ? 'static page' : 'this record'}}</span>
                              <span v-if="component.customText" class="component-detail"><span class="detail-label">text:</span> {{component.customText}}</span>
                              <span v-if="component.displayAsButton" class="component-detail"><span class="detail-label">shown as:</span> button</span>
                              <span v-if="component.valueFormatting && component.valueFormatting.type" class="component-detail"><span class="detail-label">formatting:</span> overrides field</span>
                            </div>
                          </div>
                          <span
                            @click.stop="openRuleEditor(component)"
                            class="rules-btn"
                            :class="{ 'has-rules': component.formattingRules && component.formattingRules.rules && component.formattingRules.rules.length }"
                            title="Formatting rules - add conditional styling based on field values"
                          ><span class="rules-btn-inner">&#9889;<span class="rules-btn-label">rules</span></span></span>
                          <span @click="removeComponent(column, component)" class="delete">x</span>
                        </div>
                      </template>
                    </draggable>
                    <div @click="createComponent(column)" class="add-btn">
                      + add field
                    </div>
                  </div>
                </div>
                <div @click="addLayoutColumn(row)" class="add-btn">
                  + add column
                </div>
              </div>
              <div @click="addLayoutRow()" class="add-btn">
                + add row
              </div>
            </div>
            <div v-else class="tab-section">
              <p class="section-desc">{{ fieldsIntro }}</p>
              <button
                v-if="showAddAllColumns"
                type="button"
                @click="addAllSpreadsheetColumns"
                class="add-btn add-all-columns-btn"
              >
                + add all columns from {{ spreadsheetTitle }}
              </button>
              <p v-if="cards.components && cards.components.length > 1" class="section-desc">drag the handle to change the order of fields on each card.</p>
              <div v-if="cards.components">
                <draggable
                  v-model="cards.components"
                  :item-key="columnDragKey"
                  handle=".column-drag-handle"
                  ghost-class="column-drag-ghost"
                  :animation="150"
                  :force-fallback="true"
                  :fallback-on-body="true"
                >
                  <template #item="{ element: component }">
                    <div class="form-edit-item">
                      <DragHandle
                        handle-class="column-drag-handle"
                        :reorder-id="component.uniqueTitle || component.label"
                        @move="moveColumnByKey(component, $event)"
                      />
                      <div class="editable-component" @click="editComponent(cards, component)">
                        <div class="component-type">{{component.type}}</div>
                        <div class="component-details" v-if="component.uniqueTitle || component.label || component.childPageUniqueTitle">
                          <span v-if="component.uniqueTitle || component.relatedField" class="component-detail"><span class="detail-label">field:</span> {{ columnFieldLabel(component) }}<span v-if="getColumnType(component.uniqueTitle)" class="column-type-badge">{{getColumnType(component.uniqueTitle)}}</span></span>
                          <span v-if="component.label" class="component-detail"><span class="detail-label">label:</span> {{component.label}}</span>
                          <span v-if="component.childPageUniqueTitle" class="component-detail"><span class="detail-label">links to:</span> {{component.childPageUniqueTitle}}</span>
                          <span v-if="component.linkMode" class="component-detail"><span class="detail-label">mode:</span> {{component.linkMode === 'code' ? 'custom code' : component.linkMode === 'relatedRecord' ? `related record (${component.relationshipColumn})` : component.linkMode === 'static' ? 'static page' : 'this record'}}</span>
                          <span v-if="component.customText" class="component-detail"><span class="detail-label">text:</span> {{component.customText}}</span>
                          <span v-if="component.displayAsButton" class="component-detail"><span class="detail-label">shown as:</span> button</span>
                          <span v-if="component.valueFormatting && component.valueFormatting.type" class="component-detail"><span class="detail-label">formatting:</span> overrides field</span>
                        </div>
                      </div>
                      <span
                        @click.stop="openRuleEditor(component)"
                        class="rules-btn"
                        :class="{ 'has-rules': component.formattingRules && component.formattingRules.rules && component.formattingRules.rules.length }"
                        title="Formatting rules - add conditional styling based on field values"
                      ><span class="rules-btn-inner">&#9889;<span class="rules-btn-label">rules</span></span></span>
                      <span @click="removeComponent(cards, component)" class="delete">x</span>
                    </div>
                  </template>
                </draggable>
              </div>
              <div @click="createComponent(cards)" class="add-btn">
                + add field
              </div>
            </div>
          </b-tab>

          <b-tab title="Filtering">
            <div v-if="isDetail" class="tab-section">
              <p class="section-desc">A card shows the record that opened the page. Search, filters, and sorts belong on a cards list.</p>
            </div>
            <template v-if="!isDetail">
            <div v-if="pageRecordLink" class="tab-section">
              <div class="section-heading">Page record</div>
              <label class="checkbox-label">
                <input type="checkbox" v-model="filterByPageRecord" />
                only show records related to the page's {{ pageRecordLink.pageRecordTitle }} record
              </label>
              <p class="section-desc">this page is opened from a link that carries a {{ pageRecordLink.pageRecordTitle }} record. the view keeps records related through {{ pageRecordLink.columnTitle }}.</p>
            </div>
            <div class="tab-section">
              <label class="checkbox-label">
                <input type="checkbox" v-model="searchEnabled" />
                show a search box. visitors can type text to narrow the records in this view.
              </label>
              <div v-if="searchEnabled" class="search-scope">
                <label class="checkbox-label">
                  <input type="radio" value="view" v-model="searchScope" />
                  search the fields on this view, including fields from related spreadsheets
                </label>
                <label class="checkbox-label">
                  <input type="radio" value="spreadsheet" v-model="searchScope" />
                  search every field on the spreadsheet
                </label>
                <p v-if="searchScope === 'spreadsheet'" class="section-desc search-security-note">
                  Searching every field on the spreadsheet matches columns that are not shown on this view. A visitor can learn that a hidden value exists even though that column stays off the view. Password fields are never searched.
                </p>
              </div>
            </div>
            <div class="tab-section">
              <label class="checkbox-label">
                <input type="checkbox" v-model="userFiltering" />
                allow users to filter records
              </label>
              <label v-if="userFiltering" class="checkbox-label" style="margin-top: 8px;">
                <input type="checkbox" v-model="restrictUserFiltering" />
                restrict filter fields to fields on this view. if this option is unchecked, users can filter on any field or connected field.
              </label>
            </div>
            <div v-if="userFiltering" class="tab-section">
              <div class="section-heading">Default Filters</div>
              <p class="section-desc">these filters will be displayed by default on the component, and users can change them</p>
              <PreFilterBuilder
                :filters="defaultUserFilters"
                :match-mode="defaultUserFilterMatchMode"
                :spreadsheet="spreadsheet"
                :all-spreadsheets="allSpreadsheets"
                :application-unique-title="applicationUniqueTitle"
                @update:filters="defaultUserFilters = $event"
                @update:match-mode="defaultUserFilterMatchMode = $event"
              />
            </div>
            <div class="tab-section">
              <div class="section-heading">Prefilters</div>
              <p class="section-desc">these filters will always be applied to the record set and will not be displayed on the view</p>
              <PreFilterBuilder
                :filters="preFilters"
                :match-mode="preFilterMatchMode"
                :spreadsheet="spreadsheet"
                :all-spreadsheets="allSpreadsheets"
                :application-unique-title="applicationUniqueTitle"
                @update:filters="preFilters = $event"
                @update:match-mode="preFilterMatchMode = $event"
              />
            </div>
            <FilterButtonEditor
              :buttons="filterButtons"
              :allow-multiple="allowMultipleFilterButtons"
              :spreadsheet="spreadsheet"
              :all-spreadsheets="allSpreadsheets"
              :application-unique-title="applicationUniqueTitle"
              @update:buttons="filterButtons = $event"
              @update:allow-multiple="allowMultipleFilterButtons = $event"
            />
            </template>
          </b-tab>

          <b-tab title="Sorting">
            <div v-if="isDetail" class="tab-section">
              <p class="section-desc">A card shows one record, so there is nothing to sort.</p>
            </div>
            <div v-else class="tab-section">
              <div class="section-heading">Sort Order</div>
              <p class="section-desc">records in this view are ordered by these columns, from highest precedence to lowest. each column can sort ascending or descending.</p>
              <SortBuilder
                :sorts="sorts"
                :spreadsheet="spreadsheet"
                @update:sorts="sorts = $event"
              />
            </div>
          </b-tab>

          <b-tab title="AI">
            <ViewAiPanel v-model="aiPrompt" :error="aiError || ''" :submitting="aiSubmitting" />
          </b-tab>

        </b-tabs>
      </div>
    </b-modal>
    <selectColumn ref="selectColumnRef"></selectColumn>
    <RuleEditor
      :visible="ruleEditorVisible"
      :rules="ruleEditorRules"
      :spreadsheet="spreadsheet"
      :all-spreadsheets="allSpreadsheets"
      :column-unique-title="ruleEditorColumnUniqueTitle"
      :column-data-type="ruleEditorColumnDataType"
      :application-unique-title="applicationUniqueTitle"
      @update:visible="ruleEditorVisible = $event"
      @update:rules="onRulesUpdate"
    />
  </b-container>
</template>

<script>

  import axios from 'axios'
  import selectColumn from './cardsselectcolumn.vue'
  import PreFilterBuilder from './lib/PreFilterBuilder.vue'
  import FilterButtonEditor from './lib/FilterButtonEditor.vue'
  import SortBuilder from './lib/SortBuilder.vue'
  import RuleEditor from './lib/RuleEditor.vue'
  import draggable from 'vuedraggable'
  import DragHandle from './DragHandle.vue'
  import { moveListItem } from '../../../shared/list-move.js'
  import { buildComponentsFromSpreadsheetColumns } from '../lib/spreadsheet-view-fields.js'
  import { submitViewAi } from '../lib/view-ai.js'
  import ViewAiPanel from './ViewAiPanel.vue'
  import { columnDragKey } from '../lib/column-drag-key.js'
  import { relatedViewColumnOptions, findViewColumnOption, viewColumnDataType, assignRelatedViewColumn, relatedColumnCaption } from '../../../shared/related-view-columns.js'
  import { assignViewValueFormatting } from '../../../shared/value-format.js'
  import { assignViewLink } from '../../../shared/view-fields.js'
  import { assignMultiselectDisplay } from '../../../shared/multiselect-display.js'
  import { pageRecordRelationship } from '../lib/page-record-link.js'
  import { viewEditorTabAfterColumnDialog } from '../lib/view-editor-tab.js'
  import { flattenCardFields, prepareCardLayout, assignCardColumnWidth, CARD_ROW, CARD_COLUMN, cardsPerRowFromInput, MAX_CARDS_PER_ROW } from '../../../shared/card-layout.js'
  import { columnFormatLabel, columnFormatProblem } from '../../../shared/column-format.js'

  export default {
    name: `cardsEdit`,
    data () {

      return {
        cardsEditVisible: false,
        editorTitle: 'edit cards',
        activeTab: 0,
        aiPrompt: ``,
        aiError: ``,
        aiSubmitting: false,
        aiSavedUniqueTitle: ``,
        uniqueTitle: ``,
        title: ``,
        spreadSheetColumnOptions: [],
        spreadSheetUniqueTitle: ``,
        spreadsheet: null,
        cards: {
          components: []
        },
        realtime: false,
        cardsPerRow: '',
        userFiltering: false,
        searchEnabled: false,
        searchScope: 'view',
        restrictUserFiltering: false,
        defaultUserFilters: [],
        defaultUserFilterMatchMode: 'all',
        preFilters: [],
        preFilterMatchMode: 'all',
        filterButtons: [],
        allowMultipleFilterButtons: false,
        sorts: [],
        allSpreadsheets: [],
        applicationUniqueTitle: '',
        parentDesigner: null,
        isMap: false,
        isDetail: false,
        addressColumn: ``,
        latitudeColumn: ``,
        longitudeColumn: ``,
        mapStyleUrl: ``,
        isCalendar: false,
        dateColumn: ``,
        titleColumn: ``,
        descriptionColumn: ``,
        recurrenceColumn: ``,
        calendarLayout: `month`,
        addFormUniqueTitle: ``,
        filterByPageRecord: false,
        pageRecordSpreadSheetUniqueTitle: '',
        relationshipColumnTitle: '',
        pages: [],
        pageComponents: [],
        currentPageUniqueTitle: '',
        ruleEditorVisible: false,
        ruleEditorRules: [],
        ruleEditorColumnUniqueTitle: '',
        ruleEditorColumnDataType: 'string',
        ruleEditorComponent: null,
        columnWidthSaveError: '',
        columnWidthUnitOptions: [
          { value: 'percent', text: '%' },
          { value: 'pixel', text: 'px' }
        ]
      }
    },
    components: {
      selectColumn,
      DragHandle,
      PreFilterBuilder,
      FilterButtonEditor,
      SortBuilder,
      RuleEditor,
      draggable,
      ViewAiPanel
    },
    computed: {
      spreadsheetTitle () {
        return (this.spreadsheet && this.spreadsheet.title) || this.spreadSheetUniqueTitle || 'spreadsheet'
      },
      editorNoun () {
        if (this.isCalendar) return 'calendar'
        if (this.isMap) return 'map'
        if (this.isDetail) return 'card'
        return 'cards'
      },
      isCardsLayout () {
        return !this.isMap && !this.isCalendar
      },
      maxCardsPerRow () {
        return MAX_CARDS_PER_ROW
      },
      aiTabIndex () {
        return (this.isMap || this.isCalendar) ? 5 : 4
      },
      fieldsIntro () {
        if (this.isCalendar) return 'Add fields here when they need display rules. A rule on the title or description field styles that text on the calendar.'
        if (this.isMap) return 'Each map popup will contain the following fields'
        if (this.isDetail) return 'Arrange this one record in rows and columns. Visitors see these fields and cannot edit them. Max width on a column is a share of the card or a fixed pixel width. Leave it blank and the column shares the space that is left.'
        return 'Arrange each card in rows and columns. A column here is a place on the card. Add a spreadsheet field inside it. Max width caps that column: a share of the card, or a fixed pixel width. Leave it blank and the column shares the space that is left. On the General tab, maximum cards per row sets how many cards share the page column.'
      },
      calendarLayoutOptions () {
        return [
          { value: 'month', text: 'Month' },
          { value: 'week', text: 'Week' },
          { value: 'day', text: 'Day' },
          { value: 'list', text: 'List' }
        ]
      },
      recurrenceColumnOptions () {
        return this.locationOptions(column => column.type === 'text')
      },
      formOptions () {
        const forms = []
        const walk = (components) => {
          for (const component of components || []) {
            if (!component) continue
            if (component.type === 'form' && component.uniqueTitle) {
              forms.push({ value: component.uniqueTitle, text: component.title || component.uniqueTitle })
            }
            walk(component.components)
          }
        }
        walk(this.pageComponents)
        return [{ value: '', text: 'none' }, ...forms]
      },
      dateColumnOptions () {
        const dated = this.locationOptions(column => column.type === 'dateTime')
        return [
          dated[0],
          { value: 'createdAt', text: 'Created At (system)' },
          { value: 'updatedAt', text: 'Updated At (system)' },
          ...dated.slice(1)
        ]
      },
      textColumnOptions () {
        const types = new Set(['text', 'number', 'dateTime', 'formula', 'richText', 'multiselect', 'address', 'duration', 'sequence'])
        const choices = this.locationOptions(column => types.has(column.type))
        return [
          choices[0],
          { value: 'id', text: 'ID (system)' },
          { value: 'createdAt', text: 'Created At (system)' },
          { value: 'updatedAt', text: 'Updated At (system)' },
          ...choices.slice(1)
        ]
      },
      addressColumnOptions () {
        return this.locationOptions(column => column.type === 'address')
      },
      numberColumnOptions () {
        return this.locationOptions(column => column.type === 'number' || column.type === 'duration' || column.type === 'sequence')
      },
      showAddAllColumns () {
        const existing = this.isCardsLayout
          ? flattenCardFields(this.cards.components)
          : (this.cards.components || [])
        if (existing.length) return false
        return buildComponentsFromSpreadsheetColumns(this.spreadsheet && this.spreadsheet.columns, 'cards').length > 0
      },
      pageRecordLink () {
        return pageRecordRelationship(this.allSpreadsheets, this.spreadSheetUniqueTitle, this.pageRecordSpreadSheetUniqueTitle)
      },
      dataSourceLabel () {
        const title = (this.spreadsheet && this.spreadsheet.title) || this.spreadSheetUniqueTitle || 'unknown'
        if (!this.filterByPageRecord) return title

        const link = this.pageRecordLink
        const pageRecordTitle = link ? link.pageRecordTitle : this.pageRecordSpreadSheetUniqueTitle
        const columnTitle = link ? link.columnTitle : this.relationshipColumnTitle
        let label = title
        if (pageRecordTitle) label += ` — related to page's ${pageRecordTitle} record`
        if (columnTitle) label += ` via ${columnTitle}`
        return label
      }
    },
    methods: {
      moveColumnByKey (component, direction) {

        const list = this.cards && this.cards.components

        moveListItem(list, (list || []).indexOf(component), direction)
      },
      moveFieldInColumn (column, component, direction) {

        const list = column && column.components

        moveListItem(list, (list || []).indexOf(component), direction)
      },
      columnWidthText (column) {
        const format = column && column.columnFormat
        if (!format || format.width == null || String(format.width).trim() === '') return ''
        return String(format.width)
      },
      columnWidthUnit (column) {
        const unit = column && column.columnFormat && column.columnFormat.unit
        return unit === 'pixel' ? 'pixel' : 'percent'
      },
      columnWidthLabel (column) {
        const width = this.columnWidthText(column)
        if (!width || this.columnWidthError(column)) return ''
        return columnFormatLabel({ width, unit: this.columnWidthUnit(column) })
      },
      columnWidthError (column) {
        const width = this.columnWidthText(column)
        if (!width) return ''
        return columnFormatProblem({ width, unit: this.columnWidthUnit(column) })
      },
      setColumnWidth (column, value) {
        this.columnWidthSaveError = ''
        const text = value == null ? '' : String(value).trim()
        const unit = this.columnWidthUnit(column)
        if (!text) {
          if (unit === 'pixel') column.columnFormat = { unit: 'pixel' }
          else delete column.columnFormat
          return
        }
        column.columnFormat = { width: text, unit }
      },
      setColumnUnit (column, unit) {
        this.columnWidthSaveError = ''
        const next = unit === 'pixel' ? 'pixel' : 'percent'
        const width = this.columnWidthText(column)
        column.columnFormat = width ? { width, unit: next } : { unit: next }
      },
      cardColumnWidthProblem () {
        const rows = (this.cards && this.cards.components) || []
        for (const row of rows) {
          if (!row || row.type !== CARD_ROW) continue
          for (const column of row.components || []) {
            if (!column || column.type !== CARD_COLUMN) continue
            const problem = this.columnWidthError(column)
            if (problem) return problem
            assignCardColumnWidth(column, column.columnFormat)
          }
        }
        return ''
      },
      addLayoutColumn (row) {

        if (!row.components) row.components = []

        row.components.push({ type: CARD_COLUMN, components: [] })
      },
      addLayoutRow () {

        if (!this.cards.components) this.cards.components = []

        this.cards.components.push({
          type: CARD_ROW,
          components: [{ type: CARD_COLUMN, components: [] }]
        })
      },
      getColumnType (uniqueTitle) {
        if (!uniqueTitle) return null
        const related = findViewColumnOption(this.spreadSheetColumnOptions, uniqueTitle)
        if (related && related.relatedFieldType) return related.relatedFieldType
        const systemTypes = { id: 'number', createdAt: 'dateTime', updatedAt: 'dateTime' }
        if (systemTypes[uniqueTitle]) return systemTypes[uniqueTitle]
        if (!this.spreadsheet || !this.spreadsheet.columns) return null
        const col = this.spreadsheet.columns.find(c => c.uniqueTitle === uniqueTitle)
        return col ? col.type : null
      },
      columnFieldLabel (component) {
        return relatedColumnCaption(component) || (component && component.uniqueTitle) || ''
      },
      removeComponent (parentComponent, removeComponent) {

        console.log(`components before!`)
        console.log(JSON.stringify(parentComponent.components))

        parentComponent.components = _.filter(parentComponent.components, (component) => {

          return component !== removeComponent
        })

        console.log(`components after!`)
        console.log(JSON.stringify(parentComponent.components))
      },
      async getSpreadSheet () {

        console.log(`getSpreadSheet! ${this.spreadSheetUniqueTitle}`)

        const spreadsheet = await this.$http.get(`${VUE_APP_PROXY_SERVER_URL}/spreadsheets/${this.spreadSheetUniqueTitle}`, {
          headers: { 'application-unique-title': this.applicationUniqueTitle }
        })

        console.log(`have spreadsheets!`)
        console.log(spreadsheet)

        this.spreadsheet = spreadsheet.data

        if (!this.spreadsheet.columns) {

          return
        }

        const systemFields = [
          { value: 'id', text: 'ID (system)' },
          { value: 'createdAt', text: 'Created At (system)' },
          { value: 'updatedAt', text: 'Updated At (system)' }
        ]

        this.spreadSheetColumnOptions = [
          ...systemFields,
          ...this.spreadsheet.columns.map((column) => {
            return {
              value: column.uniqueTitle,
              text: column.title
            }
          }),
          ...relatedViewColumnOptions(this.spreadsheet, this.allSpreadsheets)
        ]
      },
      locationOptions (include) {
        const columns = (this.spreadsheet && this.spreadsheet.columns) || []
        return [
          { value: '', text: 'none' },
          ...columns.filter(include).map(column => ({
            value: column.uniqueTitle,
            text: column.title || column.uniqueTitle
          }))
        ]
      },
      async showEditCardsComponent (components, spreadSheetUniqueTitle) {

        this.aiPrompt = ''
        this.aiError = ''
        this.aiSubmitting = false
        this.aiSavedUniqueTitle = this.uniqueTitle

        console.log('EVENT FIRED!!!!!!')
        console.log(components)

        const alreadyOpen = this.cardsEditVisible
        this.activeTab = viewEditorTabAfterColumnDialog(alreadyOpen, this)

        // The column dialog calls this while the editor is open, then adds the
        // field to the column that is already on screen. Rebuilding the tree
        // here would detach that column.
        if (!alreadyOpen) {
          const source = JSON.parse(JSON.stringify(components || []))
          this.cards.components = this.isCardsLayout ? prepareCardLayout(source) : source
          this.spreadSheetUniqueTitle = spreadSheetUniqueTitle
          await this.getSpreadSheet()
        }

        this.cardsEditVisible = true
      },
      addAllSpreadsheetColumns () {
        if (!this.showAddAllColumns) return
        const fields = buildComponentsFromSpreadsheetColumns(this.spreadsheet.columns, 'cards')
        if (!this.isCardsLayout) {
          if (!this.cards.components) this.cards.components = []
          this.cards.components.push(...fields)
          return
        }
        if (!this.cards.components || !this.cards.components.length) {
          this.cards.components = prepareCardLayout([])
        }
        const column = this.cards.components[0] && this.cards.components[0].components && this.cards.components[0].components[0]
        if (column) column.components.push(...fields)
      },
      async createComponent (component) {

        this.activeSlot = component

        this.$refs.selectColumnRef.parentEditor = this
        this.$refs.selectColumnRef.spreadSheetColumnOptions = this.spreadSheetColumnOptions
        this.$refs.selectColumnRef.showSelectColumnModal()
      },
      async editComponent (parentComponent, component) {

        this.activeSlot = parentComponent
        this.editingComponent = component

        this.$refs.selectColumnRef.parentEditor = this
        this.$refs.selectColumnRef.spreadSheetColumnOptions = this.spreadSheetColumnOptions
        this.$refs.selectColumnRef.showEditColumnModal(component)
      },
      editComponentInActiveSlot (selectedColumnOption, label, type, childPageUniqueTitle, customText, editableConfig, linkConfig, disableSorting, display) {

        let dataType = viewColumnDataType(this.spreadsheet, selectedColumnOption, this.spreadSheetColumnOptions)

        if (type === `link`) {

          dataType = `link`
        }

        if (type === `custom`) {

          dataType = `dataColumn`
        }

        this.editingComponent.type = dataType
        this.editingComponent.uniqueTitle = `${selectedColumnOption}`
        this.editingComponent.label = label
        this.editingComponent.childPageUniqueTitle = childPageUniqueTitle
        this.editingComponent.customText = customText

        if (linkConfig) assignViewLink(this.editingComponent, linkConfig)
        else {
          this.editingComponent.linkMode = undefined
          this.editingComponent.relationshipColumn = undefined
          delete this.editingComponent.code
          delete this.editingComponent.displayAsButton
        }

        this.stampRelatedColumn(this.editingComponent, selectedColumnOption, type)
        assignMultiselectDisplay(this.editingComponent, display && display.multiselectDisplay)
        if (display) assignViewValueFormatting(this.editingComponent, display.valueFormatting)
      },
      async addComponentToActiveSlot (selectedColumnOption, label, type, childPageUniqueTitle, customText, editableConfig, linkConfig, disableSorting, display) {

        console.log(`add form input for~~`)
        console.log(selectedColumnOption)

        let dataType = viewColumnDataType(this.spreadsheet, selectedColumnOption, this.spreadSheetColumnOptions)

        if (type === `link`) {

          dataType = `link`
        }

        if (type === `custom`) {

          dataType = `dataColumn`
        }

        const componentDef = {
          type: dataType,
          uniqueTitle: `${selectedColumnOption}`,
          label,
          childPageUniqueTitle,
          customText
        }

        if (linkConfig) assignViewLink(componentDef, linkConfig)

        this.stampRelatedColumn(componentDef, selectedColumnOption, type)
        assignMultiselectDisplay(componentDef, display && display.multiselectDisplay)
        if (display) assignViewValueFormatting(componentDef, display.valueFormatting)

        this.activeSlot.components.push(componentDef)
      },
      stampRelatedColumn (component, selectedColumnOption, type) {

        const option = type === `link` || type === `custom`
          ? null
          : findViewColumnOption(this.spreadSheetColumnOptions, selectedColumnOption)

        assignRelatedViewColumn(component, option)
      },
      openRuleEditor (component) {

        this.ruleEditorComponent = component
        this.ruleEditorColumnUniqueTitle = component.uniqueTitle || ''
        this.ruleEditorRules = (component.formattingRules && component.formattingRules.rules) ? component.formattingRules.rules : []

        let dataType = 'string'
        if (this.spreadsheet && this.spreadsheet.columns) {
          const col = this.spreadsheet.columns.find(c => c.uniqueTitle === component.uniqueTitle)
          if (col) {
            const typeMap = { text: 'string', number: 'number', dateTime: 'datetime', formula: 'string', password: 'string', richText: 'string', relationship: 'relationship', multiselect: 'string', address: 'string', duration: 'number', sequence: 'number', signature: 'signature' }
            dataType = typeMap[col.type] || 'string'
          }
        }
        this.ruleEditorColumnDataType = dataType

        this.ruleEditorVisible = true
      },
      onRulesUpdate (rules) {

        if (this.ruleEditorComponent) {
          this.ruleEditorComponent.formattingRules = { rules }
        }
      },
      columnDragKey,
      async onModalOk (event) {

        const handled = await submitViewAi(this, event, { visibleKey: 'cardsEditVisible', tabIndex: this.aiTabIndex })

        if (!handled) return this.save(event)
      },
      async save (event) {

        if (this.isCardsLayout) {
          const problem = this.cardColumnWidthProblem()
          if (problem) {
            this.columnWidthSaveError = problem
            this.activeTab = 1
            if (event && event.preventDefault) event.preventDefault()
            return
          }
          this.columnWidthSaveError = ''
        }

        console.log(`save form with cards components!`)
        console.log(this.cards.components)

        const payload = {
          uniqueTitle: this.uniqueTitle,
          title: this.title,
          spreadSheetUniqueTitle: this.spreadSheetUniqueTitle,
          components: this.cards.components,
          userFiltering: this.userFiltering,
          searchEnabled: !!this.searchEnabled,
          searchScope: this.searchScope === 'spreadsheet' ? 'spreadsheet' : 'view',
          restrictUserFiltering: this.restrictUserFiltering,
          defaultUserFilters: this.defaultUserFilters,
          defaultUserFilterMatchMode: this.defaultUserFilterMatchMode,
          preFilters: this.preFilters,
          preFilterMatchMode: this.preFilterMatchMode,
          sorts: this.sorts,
          realtime: this.realtime,
          filterByPageRecord: !!this.filterByPageRecord
        }

        if (!this.isDetail) {
          payload.filterButtons = this.filterButtons
          payload.allowMultipleFilterButtons = !!this.allowMultipleFilterButtons
        }

        if (this.isCardsLayout && !this.isDetail) {
          const count = cardsPerRowFromInput(this.cardsPerRow)
          payload.cardsPerRow = count
          payload.oneCardPerRow = count === 1
        }

        if (this.isMap) {
          payload.addressColumn = this.addressColumn || ''
          payload.latitudeColumn = this.latitudeColumn || ''
          payload.longitudeColumn = this.longitudeColumn || ''
          payload.mapStyleUrl = this.mapStyleUrl || ''
        }

        if (this.isCalendar) {
          payload.dateColumn = this.dateColumn || ''
          payload.titleColumn = this.titleColumn || ''
          payload.descriptionColumn = this.descriptionColumn || ''
          payload.recurrenceColumn = this.recurrenceColumn || ''
          payload.calendarLayout = this.calendarLayout || 'month'
          payload.addFormUniqueTitle = this.addFormUniqueTitle || ''
        }

        this.parentDesigner.saveActiveComponent(payload)
      }
    }
  }
</script>

<style>
.cards-per-row-field {
  max-width: 12rem;
}

.card-column-width {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.card-column-width-caption {
  color: #8d8d86;
  font-size: 0.72em;
  white-space: nowrap;
  margin: 0;
}

.card-column-width-input {
  width: 5.5rem;
  flex: 0 0 5.5rem;
}

.card-column-width-unit {
  width: 4.75rem;
  flex: 0 0 4.75rem;
}

.card-column-width-label {
  margin-left: 6px;
  color: #c8e88a;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}

.card-column-width-error {
  color: #f82471;
  font-size: 0.75em;
  margin: 0 0 8px;
}
</style>
